
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { BorderConfiguration } from "@/types/block-configurations";
import { ColorPicker } from "./ColorPicker";

interface BorderControlsProps {
  value: BorderConfiguration;
  onChange: (value: BorderConfiguration) => void;
  showRadius?: boolean;
}

/**
 * BorderControls Component
 *
 * Controls for border styling including width, style, color, and radius.
 * Includes a live preview of the border configuration.
 *
 * @param value - Current border configuration
 * @param onChange - Callback when border changes
 * @param showRadius - Whether to show the radius slider (default: true); hide for blocks whose renderer ignores radius
 */
export function BorderControls({ value, onChange, showRadius = true }: BorderControlsProps) {
  const updateField = <K extends keyof BorderConfiguration>(
    field: K,
    newValue: BorderConfiguration[K]
  ) => {
    onChange({ ...value, [field]: newValue });
  };

  return (
    <div className="space-y-2.5">
      {/* Border Width */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="text-xs font-medium">Border Width</Label>
          <span className="text-xs text-muted-foreground">{value.width}px</span>
        </div>
        <Slider
          value={[value.width]}
          onValueChange={([newValue]) => updateField('width', newValue)}
          min={0}
          max={10}
          step={1}
        />
      </div>

      {/* Border Style */}
      <div>
        <Label className="text-xs font-medium mb-1 block">Border Style</Label>
        <Select
          value={value.style}
          onChange={(e) => updateField('style', e.target.value as BorderConfiguration['style'])}
        >
          <option value="solid">Solid</option>
          <option value="dashed">Dashed</option>
          <option value="dotted">Dotted</option>
          <option value="none">None</option>
        </Select>
      </div>

      {/* Border Color (only show if width > 0) */}
      {value.width > 0 && value.style !== 'none' && (
        <div>
          <Label className="text-xs font-medium mb-1 block">Border Color</Label>
          <ColorPicker
            value={value.color}
            onChange={(color) => updateField('color', color ?? '')}
          />
        </div>
      )}

      {/* Border Radius */}
      {showRadius && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-xs font-medium">Border Radius</Label>
            <span className="text-xs text-muted-foreground">{value.radius}px</span>
          </div>
          <Slider
            value={[value.radius]}
            onValueChange={([newValue]) => updateField('radius', newValue)}
            min={0}
            max={50}
            step={1}
          />
        </div>
      )}
    </div>
  );
}
